// Floating panel shown over the 3D viewer when the user clicks a spool/mark in MTM demand mode.
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { X, Loader2, ShieldCheck, AlertTriangle, TrendingDown, Package, Lock, Calendar } from 'lucide-react';

const STATUS_PALETTE = {
  RESERVED:    { bg: '#22c55e', label: 'Material Reserved',  Icon: ShieldCheck },
  OPEN_DEMAND: { bg: '#f59e0b', label: 'Open Demand',        Icon: TrendingDown },
  STOCKOUT:    { bg: '#ef4444', label: 'Stockout Blocked',   Icon: AlertTriangle },
  NO_SIGNAL:   { bg: '#475569', label: 'No Active Signal',   Icon: Package },
};

export default function MTMElementPopover({ projectId, module, entityType, entityValue, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId || !entityValue) return;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/workfront-model/${module}/mtm-element-detail`, {
      params: { entityType, entityValue },
    }).then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [projectId, module, entityType, entityValue]);

  const status = data?.overallStatus || 'NO_SIGNAL';
  const meta = STATUS_PALETTE[status] || STATUS_PALETTE.NO_SIGNAL;
  const StatusIcon = meta.Icon;

  return (
    <div className="absolute top-3 right-3 w-[400px] max-h-[90%] overflow-y-auto rounded-lg shadow-2xl"
      style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', zIndex: 50 }}
      data-testid="mtm-element-popover">
      {/* Header */}
      <div className="flex items-start justify-between p-3" style={{ background: meta.bg, color: '#fff' }}>
        <div className="flex items-start gap-2">
          <StatusIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-80">
              {entityType === 'spool' ? 'Piping Spool' :
                entityType === 'mark' ? 'Structural Mark' :
                entityType === 'line' ? 'Piping Line (aggregate)' :
                entityType === 'tag' ? 'Structural Tag (aggregate)' : 'Element'}
            </div>
            <div className="text-sm font-bold font-mono">{entityValue}</div>
            <div className="text-[11px] mt-0.5">{meta.label}</div>
            {Array.isArray(data?.childSpools) && data.childSpools.length > 0 && (
              <div className="text-[9px] mt-0.5 opacity-80">rolled up from {data.childSpools.length} spool(s)</div>
            )}
            {Array.isArray(data?.childMarks) && data.childMarks.length > 0 && (
              <div className="text-[9px] mt-0.5 opacity-80">rolled up from {data.childMarks.length} mark(s)</div>
            )}
          </div>
        </div>
        <button onClick={onClose} data-testid="mtm-popover-close"
          className="p-1 rounded hover:bg-white/20"><X className="w-4 h-4 text-white" /></button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
      ) : !data ? (
        <div className="p-3 text-[11px]" style={{ color: 'var(--text-muted)' }}>Could not load element details.</div>
      ) : (
        <div className="p-3 space-y-3">
          {/* Linked context */}
          <LinkedCtx data={data} module={module} />

          {/* Reservations */}
          {data.reservations?.length > 0 && (
            <Section title={`Reservations (${data.reservations.length})`} color="#22c55e">
              {data.reservations.map((r, i) => (
                <div key={i} className="p-2 rounded text-[11px]" style={{ background: '#22c55e10', border: '1px solid #22c55e40' }}
                  data-testid={`mtm-popover-res-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold" style={{ color: '#143A6B' }}>{r.materialCode}</span>
                    <span className="text-[10px] font-bold" style={{ color: '#22c55e' }}>
                      <Lock className="w-2.5 h-2.5 inline" /> {r.qtyReserved} {r.uom}
                    </span>
                  </div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{r.description}</div>
                  <div className="text-[9px] mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    by {r.reservedBy || '—'}
                  </div>
                </div>
              ))}
            </Section>
          )}

          {/* Demand */}
          {data.demand?.length > 0 && (
            <Section title={`Open Material Demand (${data.demand.length})`} color="#f59e0b">
              {data.demand.map((d, i) => {
                const blocked = d.stockoutBlocked;
                const conf = d.matchConfidence || 'LOW';
                return (
                  <div key={i} className="p-2 rounded text-[11px]" data-testid={`mtm-popover-dem-${i}`}
                    style={{ background: blocked ? '#ef444410' : '#f59e0b10', border: `1px solid ${blocked ? '#ef444440' : '#f59e0b40'}` }}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        <span className="px-1.5 py-0 mr-1 rounded text-[9px] font-bold" style={{ background: d.priority === 1 ? '#ef4444' : '#f59e0b', color: '#fff' }}>P{d.priority}</span>
                        {d.componentDescription}
                      </span>
                      <span className="text-[10px] font-mono font-bold" style={{ color: blocked ? '#ef4444' : '#f59e0b' }}>
                        {d.shortfallQty} {d.uom}
                      </span>
                    </div>
                    <div className="text-[10px] mt-1 flex items-center justify-between">
                      <span style={{ color: 'var(--text-muted)' }}>
                        → <strong style={{ color: '#143A6B' }}>{d.matchedMaterialCode}</strong> {d.matchedDescription}
                      </span>
                      <span className="px-1 rounded text-[8px] font-bold"
                        style={{
                          background: conf === 'HIGH' ? '#22c55e30' : conf === 'MEDIUM' ? '#eab30830' : '#94a3b830',
                          color: conf === 'HIGH' ? '#22c55e' : conf === 'MEDIUM' ? '#eab308' : '#94a3b8',
                        }}>{conf}</span>
                    </div>
                    <div className="text-[9px] mt-1 flex items-center justify-between" style={{ color: 'var(--text-muted)' }}>
                      <span>Avail: <strong>{d.availableInStock} {d.uom}</strong> {d.warehouseCode ? `· WH ${d.warehouseCode}` : ''}</span>
                      {d.materialForecast?.projectedStockoutDate && (
                        <span><Calendar className="w-2.5 h-2.5 inline" /> {d.materialForecast.projectedStockoutDate}</span>
                      )}
                    </div>
                    {blocked && d.materialForecast?.label && (
                      <div className="text-[9px] mt-1 font-semibold" style={{ color: '#ef4444' }}>
                        🚫 {d.materialForecast.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </Section>
          )}

          {/* Totals */}
          {(data.totalReservedValue > 0 || data.totalDemandValue > 0) && (
            <div className="p-2 rounded text-[11px] grid grid-cols-2 gap-2"
              style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}>
              <div>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Reserved Value</div>
                <div className="font-bold" style={{ color: '#22c55e' }}>${data.totalReservedValue.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Open Demand Value</div>
                <div className="font-bold" style={{ color: '#f59e0b' }}>${data.totalDemandValue.toLocaleString()}</div>
              </div>
            </div>
          )}

          {!data.reservations?.length && !data.demand?.length && (
            <div className="text-center py-6 text-[11px]" style={{ color: 'var(--text-muted)' }}>
              No active MTM signal for this {entityType}.
              <div className="text-[10px] mt-1">All material is either fully issued or none is required.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LinkedCtx({ data, module }) {
  const ctx = data.linkedDocs || {};
  return (
    <div className="text-[10px] grid grid-cols-2 gap-2 p-2 rounded" style={{ background: 'var(--card-bg-alt)' }}>
      {module === 'piping' && ctx.line && (
        <>
          <KV label="Line" value={<span className="font-mono">{ctx.line.lineId}</span>} />
          <KV label="Priority" value={`P${ctx.line.priority || '?'}`} />
          <KV label="Area" value={ctx.line.area || '—'} />
          <KV label="AG/UG" value={ctx.line.agUg || '—'} />
        </>
      )}
      {module === 'piping' && ctx.spool && (
        <>
          <KV label="Fab Stage" value={ctx.spool.currentFabStage} />
          <KV label="Inch-Dia" value={ctx.spool.spoolInchDia} />
        </>
      )}
      {module === 'structural' && ctx.tag && (
        <>
          <KV label="Tag" value={<span className="font-mono">{ctx.tag.tagNumber}</span>} />
          <KV label="Priority" value={`P${ctx.tag.priority || '?'}`} />
          <KV label="Area" value={ctx.tag.area || '—'} />
        </>
      )}
      {module === 'structural' && ctx.mark && (
        <>
          <KV label="Fab Stage" value={ctx.mark.currentFabStage} />
          <KV label="Weight" value={`${ctx.mark.weightTons || 0} T`} />
        </>
      )}
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="font-semibold" style={{ color: 'var(--text-primary)' }}>{value ?? '—'}</div>
    </div>
  );
}
